import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  Radio,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search,
  PlayArrow,
  ShoppingCart,
  Assessment,
  BarChart as BarChartIcon,
  AccessTime,
  Quiz as QuizIcon,
  Replay,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import { testerHasPermission } from "../../../utils/testerPermissions";
import useExamMode from "../../../hooks/useExamMode";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

/** Matches backend config/constants.php mock_exam_purchase_status */
const PP_STATUS = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
};

const TAB_PAPERS = 0;
const TAB_PURCHASED = 1;

const StudentPastPapers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTestingMode } = useExamMode();
  const isPractice = isTestingMode("past_papers");

  const tabFromUrl = searchParams.get("tab") === "purchased" ? TAB_PURCHASED : TAB_PAPERS;
  const [tab, setTab] = useState(tabFromUrl);

  useEffect(() => {
    setTab(searchParams.get("tab") === "purchased" ? TAB_PURCHASED : TAB_PAPERS);
  }, [searchParams]);

  const handleTab = (_, v) => {
    setTab(v);
    setSearchParams(v === TAB_PURCHASED ? { tab: "purchased" } : {});
  };

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [purchasing, setPurchasing] = useState(null);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPagination, setCatalogPagination] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
  const inProgressToastShownRef = useRef(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    api.get("/common/data", { params: { param: "Subjects" } })
      .then((subRes) => setAllSubjects((subRes.data?.data || []).map((s) => s.name).filter(Boolean).sort()))
      .catch(() => {});
    api.get("/student/past-papers/years")
      .then((res) => setYearOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, []);

  const fetchCatalog = useCallback(async (page = 1, extraParams = {}) => {
    setCatalogLoading(true);
    setLoadError(false);
    try {
      const res = await api.get("/student/past-papers/catalog", {
        params: { per_page: 20, page, include_completed: 1, ...extraParams },
      });
      const payload = res.data?.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setCatalog([...list].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setCatalogPagination({
        current_page: payload?.current_page ?? page,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 20,
        total: payload?.total ?? 0,
      });
      if (!inProgressToastShownRef.current && list.some((p) => p.has_purchase && p.purchase_status === PP_STATUS.IN_PROGRESS)) {
        inProgressToastShownRef.current = true;
        toast.info("You have a paper in progress. Resume it from the card below.", {
          toastId: "in-progress-paper",
          autoClose: 5000,
        });
      }
    } catch {
      toast.error("Failed to load past papers");
      setCatalog([]);
      setLoadError(true);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (filterCategory) params.category = filterCategory;
    if (filterSubject) params.subject = filterSubject;
    if (filterYear) params.year = filterYear;
    fetchCatalog(catalogPage, params);
  }, [fetchCatalog, catalogPage, search, filterCategory, filterSubject, filterYear]);

  useEffect(() => {
    setCatalogPage(1);
  }, [search, filterCategory, filterSubject, filterYear]);

  useEffect(() => {
    const focusId = new URLSearchParams(location.search || "").get("focusPastPaper");
    if (!focusId || tab !== TAB_PAPERS || catalogLoading || catalog.length === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const safeId = String(focusId).replace(/[^0-9]/g, "");
      if (!safeId) return;
      const el = document.querySelector(`[data-past-paper-id="${safeId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.search, tab, catalogLoading, catalog]);

  const categories = useMemo(() => {
    const names = new Set();
    catalog.forEach((p) => {
      if (p.category) names.add(p.category);
    });
    return Array.from(names)
      .sort()
      .map((name) => ({ id: name, name }));
  }, [catalog]);

  const subjectOptions = useMemo(() => {
    if (allSubjects.length > 0) return allSubjects;
    const names = new Set();
    catalog.forEach((p) => {
      (p.subjects ?? []).forEach((s) => { if (s) names.add(s); });
    });
    return Array.from(names).sort();
  }, [allSubjects, catalog]);

  const autoSelectedCategoryRef = useRef(false);
  useEffect(() => {
    if (categories.length === 0 || autoSelectedCategoryRef.current) return;
    const match = categories.find((c) => c.name.toLowerCase().includes('past paper'));
    if (match) {
      setFilterCategory(match.name);
      autoSelectedCategoryRef.current = true;
    }
  }, [categories]);

  const filteredCatalog = catalog.filter((p) => p.status == null || p.status === 2);

  const [resumeDialog, setResumeDialog] = useState(null); // { purchaseId, paperName }
  const [resetting, setResetting] = useState(false);

  const openResumeDialog = (purchaseId, paperName) => {
    setResumeDialog({ purchaseId, paperName });
  };

  const handleContinue = () => {
    if (!resumeDialog) return;
    const id = resumeDialog.purchaseId;
    setResumeDialog(null);
    navigate(`/${STUDENT_BASE}/past-paper/${id}/take`);
  };

  const handleStartFresh = async () => {
    if (!resumeDialog) return;
    const id = resumeDialog.purchaseId;
    setResetting(true);
    try {
      const res = await api.post(`/student/paper/${id}/reset`);
      const newId = res.data?.data?.purchase_id ?? id;
      try { localStorage.removeItem(`paper_draft_${id}`); } catch {}
      setResumeDialog(null);
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${newId}/take`, { state: { startFresh: true } });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not reset paper");
    } finally {
      setResetting(false);
    }
  };

  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const [paperSummaries, setPaperSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [resultSearch, setResultSearch] = useState("");
  const [resultSearchSubmitted, setResultSearchSubmitted] = useState("");
  const [summaryPagination, setSummaryPagination] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 });

  useEffect(() => {
    if (isPractice && !showPracticeResults) {
      setShowPracticeResults(true);
      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
    } else if (!isPractice && showPracticeResults) {
      setShowPracticeResults(false);
      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  }, [isPractice, showPracticeResults]);

  const fetchPaperSummaries = useCallback(async (page = 1, searchTerm = "", includeTest = false) => {
    setSummariesLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (searchTerm) params.search = searchTerm;
      if (includeTest) params.include_test = 1;
      const res = await api.get("/student/paper/results-by-paper", { params });
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setPaperSummaries(list);
      setSummaryPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 25,
        total: payload?.total ?? 0,
      });
    } catch {
      toast.error("Failed to load results");
      setPaperSummaries([]);
    } finally {
      setSummariesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === TAB_PURCHASED) {
      fetchPaperSummaries(summaryPagination.current_page, resultSearchSubmitted, showPracticeResults);
    }
  }, [tab, resultSearchSubmitted, summaryPagination.current_page, showPracticeResults, fetchPaperSummaries]);

  const handleResultSearch = (e) => {
    e?.preventDefault();
    setResultSearchSubmitted(resultSearch);
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleResultClear = () => {
    setResultSearch("");
    setResultSearchSubmitted("");
    setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const formatResultDate = (iso) => {
    if (!iso) return "N/A";
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const isPaperFree = (paper) => {
    if (paper.is_free_paper) return true;
    const n = parseFloat(String(paper.price ?? "").replace(",", "."));
    return !Number.isNaN(n) && n <= 0;
  };

  const canClaimFree = (paper) => !paper.has_purchase && isPaperFree(paper) && !paper.stripe_price_id;

  const handleClaimFree = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post("/student/paper/claim-free", { paper_id: paper.id });
      const pid = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      toast.success("Paper added to your account.");
      await fetchCatalog(catalogPage);
      if (pid) {
        navigate(`/${STUDENT_BASE}/past-paper/${pid}/take`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not add this paper");
    } finally {
      setPurchasing(null);
    }
  };

  const handlePurchase = async (paper) => {
    if (!paper.stripe_price_id) {
      toast.error("This paper is not available for online purchase yet.");
      return;
    }
    setPurchasing(paper.id);
    try {
      const res = await api.post("/student/paper/purchase", { paper_id: paper.id });
      const data = res.data?.data ?? res.data;
      const url = data?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const isInProgress = (paper) =>
    paper.has_purchase &&
    paper.purchase_status === PP_STATUS.IN_PROGRESS &&
    !paper.purchase_completed_at;

  const isCompleted = (paper) =>
    paper.has_purchase && paper.purchase_status === PP_STATUS.COMPLETED;

  const handleRetake = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post(`/student/paper/${paper.purchase_id}/reset`);
      const newId = res.data?.data?.purchase_id ?? paper.purchase_id;
      try { localStorage.removeItem(`paper_draft_${paper.purchase_id}`); } catch {}
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${newId}/take`, { state: { startFresh: true } });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not retake paper");
    } finally {
      setPurchasing(null);
    }
  };

  const handleVerifierStart = async (paper) => {
    setPurchasing(paper.id);
    try {
      const res = await api.post(`/student/paper/${paper.id}/start`);
      const pid = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      if (!pid) {
        toast.error("Could not start paper");
        return;
      }
      await fetchCatalog(catalogPage);
      navigate(`/${STUDENT_BASE}/past-paper/${pid}/take`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not start paper");
    } finally {
      setPurchasing(null);
    }
  };

  const canVerifyPaper = testerHasPermission("verify-paper");

  if (catalogLoading && tab === TAB_PAPERS) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (loadError && tab === TAB_PAPERS) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 420 }}>
          Unable to load past papers. Please check your connection and try again.
        </Alert>
        <Button variant="contained" onClick={fetchCatalog}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Tabs
        value={tab}
        onChange={handleTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab
          label="Paper"
          value={TAB_PAPERS}
          icon={<QuizIcon />}
          iconPosition="start"
        />
        <Tab
          label="Result"
          value={TAB_PURCHASED}
          icon={<BarChartIcon />}
          iconPosition="start"
        />
      </Tabs>

      {isPractice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — Practice freely. Answers will not be graded.
        </Alert>
      )}

      {tab === TAB_PAPERS && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {isPractice ? "Practice Papers" : "Available Papers"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isPractice
                ? "Practice mode is active. Your results will not be recorded."
                : "Choose a paper to start, resume, or review your result."}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search papers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {categories.length > 0 && (
              <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 200 }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                  renderValue={(val) => {
                    if (!val) return "All Categories";
                    return val;
                  }}
                >
                  <MenuItem value="">
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio size="small" checked={!filterCategory} />
                    </ListItemIcon>
                    All Categories
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio size="small" checked={filterCategory === cat.name} />
                      </ListItemIcon>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {subjectOptions.length > 0 && (
              <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 180 }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={filterSubject}
                  label="Subject"
                  onChange={(e) => setFilterSubject(e.target.value)}
                  renderValue={(val) => val || "All Subjects"}
                >
                  <MenuItem value="">
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio size="small" checked={!filterSubject} />
                    </ListItemIcon>
                    All Subjects
                  </MenuItem>
                  {subjectOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio size="small" checked={filterSubject === s} />
                      </ListItemIcon>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {yearOptions.length > 0 && (
              <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 160 }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={filterYear}
                  label="Year"
                  onChange={(e) => setFilterYear(e.target.value)}
                  renderValue={(val) => val || "All Years"}
                >
                  <MenuItem value="">
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio size="small" checked={!filterYear} />
                    </ListItemIcon>
                    All Years
                  </MenuItem>
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio size="small" checked={filterYear === y} />
                      </ListItemIcon>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {filteredCatalog.some((p) => isInProgress(p)) && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                mb: 3,
                borderRadius: 2,
                bgcolor: "#ede9fe",
                color: "#5b21b6",
                border: "1px solid #c4b5fd",
                "& .MuiAlert-message": { fontWeight: 600 },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                You have{" "}
                {filteredCatalog.filter((e) => isInProgress(e)).length === 1 ? "a paper" : "papers"} in progress.
                Resume from the card below to continue where you left off.
              </Typography>
            </Alert>
          )}

          {filteredCatalog.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No past papers are currently available
              {search || filterCategory || filterSubject || filterYear ? " matching your filters" : " to you"}.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {filteredCatalog.map((paper) => {
                const isPur = purchasing === paper.id;
                const inProg = isInProgress(paper);
                const qCount = paper.extract_questions_count || 0;

                const hasOwnedAttempt = paper.has_purchase && !!paper.purchase_id;
                const showPurchasePrimary = !paper.has_purchase && !!paper.stripe_price_id;
                const showFreePrimary = !paper.has_purchase && canClaimFree(paper);

                const primaryConfig = (() => {
                  if (hasOwnedAttempt) {
                    if (inProg) {
                      return {
                        label: "Resume",
                        startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                        onClick: () => openResumeDialog(paper.purchase_id, paper.name),
                        bg: "linear-gradient(90deg, #667eea, #764ba2)",
                      };
                    }
                    if (isCompleted(paper)) {
                      return {
                        label: isPractice ? "Practice Again" : "Retake",
                        startIcon: <Replay sx={{ fontSize: 16 }} />,
                        onClick: () => handleRetake(paper),
                        bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                      };
                    }
                    return {
                      label: isPractice ? "Start Practice" : "Start",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => navigate(`/${STUDENT_BASE}/past-paper/${paper.purchase_id}/take`),
                      bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                    };
                  }
                  if (showFreePrimary) {
                    return {
                      label: "Start free paper",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => handleClaimFree(paper),
                      bg: "linear-gradient(90deg, #16a34a, #15803d)",
                    };
                  }
                  if (showPurchasePrimary) {
                    return {
                      label: "Purchase",
                      startIcon: <ShoppingCart sx={{ fontSize: 16 }} />,
                      onClick: () => handlePurchase(paper),
                      bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                    };
                  }
                  if (paper.verifier_access || paper.mobile_access || canVerifyPaper) {
                    return {
                      label: isPractice ? "Start Practice" : "Start",
                      startIcon: <PlayArrow sx={{ fontSize: 16 }} />,
                      onClick: () => (paper.purchase_id
                        ? navigate(`/${STUDENT_BASE}/past-paper/${paper.purchase_id}/take`)
                        : handleVerifierStart(paper)),
                      bg: "linear-gradient(90deg, #1a1a1a, #3a3a3a)",
                    };
                  }
                  return null;
                })();

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={paper.id}>
                    <Card
                      data-past-paper-id={paper.id}
                      sx={{
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        maxWidth: 360,
                        mx: "auto",
                        transition: "box-shadow 0.2s",
                        border: inProg ? "2px solid #7c3aed" : "1px solid #e0e0e0",
                        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, pb: "10px !important", display: "flex", flexDirection: "column", flex: 1 }}>
                        {inProg && (
                          <Box sx={{ mb: 0.75 }}>
                            <Chip
                              label="In Progress"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                height: 22,
                                bgcolor: "#ede9fe",
                                color: "#5b21b6",
                                border: "1px solid #c4b5fd",
                              }}
                            />
                          </Box>
                        )}

                        <Tooltip title={paper.name || ""} placement="top" enterDelay={400}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              lineHeight: 1.35,
                              mb: 0.5,
                              minHeight: "3.2em",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {paper.name}
                          </Typography>
                        </Tooltip>

                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1, alignItems: "center" }}>
                          <Tooltip title="Questions in this paper">
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                              <QuizIcon sx={{ fontSize: 13 }} />
                              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                                {qCount}
                                <Box component="span" sx={{ ml: 0.25 }}>
                                  Q
                                </Box>
                              </Box>
                            </Typography>
                          </Tooltip>
                          {paper.duration_minutes ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              <AccessTime sx={{ fontSize: 13 }} /> {paper.duration_minutes}m
                            </Typography>
                          ) : null}
                          {isPractice && (
                            <Chip label="Unlimited Practice" size="small" color="info" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {paper.is_free_paper && <Chip size="small" label="Free" color="success" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />}
                          {paper.has_purchase && !inProg && !isCompleted(paper) && (
                            <Chip size="small" label="Owned" color="primary" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                          {isCompleted(paper) && (
                            <Chip size="small" label="Completed" color="success" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }} />
                          )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {paper.category || "—"}
                          {paper.format ? ` · ${paper.format}` : ""}
                        </Typography>

                        <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                          {primaryConfig && (
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              disabled={isPur}
                              startIcon={isPur ? <CircularProgress size={14} color="inherit" /> : primaryConfig.startIcon}
                              onClick={primaryConfig.onClick}
                              sx={{
                                background: primaryConfig.bg,
                                textTransform: "none",
                                fontWeight: 700,
                                py: 0.6,
                                borderRadius: 1.5,
                                fontSize: "0.78rem",
                                "&:hover": { opacity: 0.9 },
                                "&:disabled": { background: "#ccc", color: "white" },
                              }}
                            >
                              {isPur ? "Starting…" : primaryConfig.label}
                            </Button>
                          )}
                          {!paper.stripe_price_id && !isPaperFree(paper) && !paper.has_purchase && (
                            <Typography variant="caption" color="text.secondary">
                              Not available for online purchase yet.
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {catalogPagination.last_page > 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={catalogPage <= 1 || catalogLoading}
                onClick={() => setCatalogPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {catalogPagination.current_page} of {catalogPagination.last_page}
                {catalogPagination.total > 0 && ` · ${catalogPagination.total} papers`}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={catalogPage >= catalogPagination.last_page || catalogLoading}
                onClick={() => setCatalogPage((p) => Math.min(catalogPagination.last_page, p + 1))}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {tab === TAB_PURCHASED && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                My Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One row per paper with your average score. Open Report to see every attempt.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3, alignItems: "center" }}>
              <Box component="form" onSubmit={handleResultSearch} sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search results"
                  value={resultSearch}
                  onChange={(e) => setResultSearch(e.target.value)}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" size="medium">Search</Button>
              </Box>
              {resultSearchSubmitted && (
                <Button variant="outlined" size="small" onClick={handleResultClear}>Clear</Button>
              )}
              {isPractice && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                  <Typography variant="body2" color="text.secondary">Show practice results</Typography>
                  <input
                    type="checkbox"
                    checked={showPracticeResults}
                    onChange={(e) => {
                      setShowPracticeResults(e.target.checked);
                      setSummaryPagination((prev) => ({ ...prev, current_page: 1 }));
                    }}
                  />
                </Box>
              )}
            </Box>

            {summariesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : paperSummaries.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, border: "2px dashed", borderColor: "divider", borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Results Found
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Complete a paper to see your results here.
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Paper</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last completed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Attempts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Latest score</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paperSummaries.map((row) => (
                        <TableRow key={row.paper_id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.paper_name}</Typography>
                              {isPractice && showPracticeResults && (
                                <Chip
                                  size="small"
                                  label="Practice"
                                  color="info"
                                  sx={{ fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatResultDate(row.last_completed_at)}</Typography>
                            {row.attempts_count > 1 && (
                              <Typography variant="caption" color="text.secondary">{row.attempts_count} attempts</Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.attempts_count}</TableCell>
                          <TableCell>
                            {(() => {
                              const hasAi = row.last_ai_feedback_status === "completed" && row.last_ai_score != null;
                              const pct = hasAi ? row.last_ai_percentage : row.last_percentage;
                              const score = hasAi ? row.last_ai_score : row.last_score;
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Tooltip title={hasAi ? "AI-adjusted score" : ""}>
                                    <Chip
                                      size="small"
                                      icon={hasAi
                                        ? <Assessment sx={{ fontSize: 13 }} />
                                        : <BarChartIcon sx={{ fontSize: 13 }} />}
                                      label={`${Number(pct ?? 0).toFixed(1)}%`}
                                      color={Number(pct) >= 50 ? "success" : "error"}
                                      sx={{ fontWeight: 600, alignSelf: "flex-start" }}
                                    />
                                  </Tooltip>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                                    {Number(score ?? 0).toFixed(1)} / {Number(row.last_total_marks ?? 0).toFixed(0)} marks
                                    {hasAi && <Typography component="span" variant="caption" sx={{ color: "#7c3aed", ml: 0.5 }}>(AI)</Typography>}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/${STUDENT_BASE}/past-paper-report/${row.paper_id}`, {
                                state: { includePracticeResults: showPracticeResults },
                              })}
                            >
                              Report
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                  <Button
                    variant="outlined" size="small"
                    disabled={summaryPagination.current_page <= 1}
                    onClick={() => setSummaryPagination((prev) => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    disabled={summaryPagination.current_page >= summaryPagination.last_page}
                    onClick={() => setSummaryPagination((prev) => ({ ...prev, current_page: Math.min(prev.last_page, prev.current_page + 1) }))}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!resumeDialog} onClose={() => !resetting && setResumeDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resume or start fresh?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{resumeDialog?.paperName}</strong> has an attempt in progress. Would you like to continue where you left off, or start a fresh attempt?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: "column", alignItems: "stretch" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleContinue}
            disabled={resetting}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            Continue where I left off
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={handleStartFresh}
            disabled={resetting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {resetting ? <CircularProgress size={18} color="inherit" /> : "Start fresh (clears previous answers)"}
          </Button>
          <Button fullWidth onClick={() => setResumeDialog(null)} disabled={resetting} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentPastPapers;
